/**
 * useLiveFeed.ts · WebSocket-first live feed with polling fallback
 *
 * Contract:
 *   1. Open ws://<base>/ws/vehicles on mount.
 *   2. Every message is a `LiveFeedEnvelope`; update state.
 *   3. On close/error, degrade to polling GET /api/vehicles/live every 3s.
 *   4. Auto-reconnect the WebSocket with exponential backoff (1s → 8s cap).
 *   5. Send `{"type":"ping"}` every 30s while connected; ignore any pongs.
 *
 * `connectionState` drives the header dot: `connected` (green pulse) ·
 * `polling` (cool-ash static) · `disconnected` (clay ember).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBaseUrl, getVehiclesLive, getWebSocketUrl, isAbortError, } from "../lib/api";
// ---- helpers ------------------------------------------------------
/** Empty envelope so consumers can render an "awaiting first frame" state. */
function emptyEnvelope() {
    return {
        simulated: true,
        generated_at: new Date(0).toISOString(),
        entities: [],
        alerts: [],
    };
}
/** Coerce anything the backend might send into a well-typed envelope. */
function coerce(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    if (!Array.isArray(o.entities))
        return null;
    const alerts = Array.isArray(o.alerts) ? o.alerts : [];
    return {
        simulated: Boolean(o.simulated),
        generated_at: typeof o.generated_at === "string" ? o.generated_at : new Date().toISOString(),
        entities: o.entities,
        alerts: alerts,
    };
}
// ---- hook ---------------------------------------------------------
export function useLiveFeed(options = {}) {
    const { wsUrl: providedUrl, pollingIntervalMs = 3_000, backoffBaseMs = 1_000, backoffMaxMs = 8_000, pingIntervalMs = 30_000, enabled = true, } = options;
    const [envelope, setEnvelope] = useState(null);
    const [connectionState, setConnectionState] = useState("connecting");
    const [lastError, setLastError] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    // Refs so we don't retrigger the effect on every reconnect tick.
    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const pingTimerRef = useRef(null);
    const pollTimerRef = useRef(null);
    const abortRef = useRef(null);
    const attemptsRef = useRef(0);
    const mountedRef = useRef(true);
    const wsSupportedRef = useRef(typeof WebSocket !== "undefined");
    const wsUrl = useMemo(() => providedUrl ?? getWebSocketUrl("/ws/vehicles"), [providedUrl]);
    const clearTimers = useCallback(() => {
        if (reconnectTimerRef.current !== null) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (pingTimerRef.current !== null) {
            window.clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }
        if (pollTimerRef.current !== null) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);
    // Poll fallback — REST every N ms while WS is unhealthy.
    const startPolling = useCallback(() => {
        if (pollTimerRef.current !== null)
            return;
        const tick = async () => {
            if (!mountedRef.current)
                return;
            abortRef.current?.abort();
            const ac = new AbortController();
            abortRef.current = ac;
            try {
                const data = await getVehiclesLive({ signal: ac.signal });
                if (!mountedRef.current)
                    return;
                setEnvelope((prev) => {
                    const next = coerce(data) ?? prev ?? emptyEnvelope();
                    return next;
                });
                setLastError(null);
                setConnectionState("polling");
            }
            catch (err) {
                if (isAbortError(err) || !mountedRef.current)
                    return;
                setConnectionState("disconnected");
                setLastError(err instanceof Error ? err.message : String(err));
            }
        };
        // Fire immediately so we get a first frame within a tick.
        void tick();
        pollTimerRef.current = window.setInterval(tick, pollingIntervalMs);
    }, [pollingIntervalMs]);
    // Attempt a fresh WebSocket connection.
    const connect = useCallback(() => {
        if (!enabled)
            return;
        if (!wsSupportedRef.current) {
            startPolling();
            return;
        }
        // Clear any prior socket + timers cleanly.
        if (wsRef.current) {
            try {
                wsRef.current.close();
            }
            catch {
                /* noop */
            }
            wsRef.current = null;
        }
        setConnectionState("connecting");
        let ws;
        try {
            ws = new WebSocket(wsUrl);
        }
        catch (err) {
            setConnectionState("disconnected");
            setLastError(err instanceof Error ? err.message : String(err));
            // Backoff + polling in parallel — REST keeps the panel alive.
            startPolling();
            scheduleReconnect();
            return;
        }
        wsRef.current = ws;
        ws.onopen = () => {
            if (!mountedRef.current)
                return;
            setConnectionState("connected");
            setLastError(null);
            attemptsRef.current = 0;
            setReconnectAttempts(0);
            // Stop polling — WS wins.
            if (pollTimerRef.current !== null) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            // Start ping heartbeat.
            if (pingTimerRef.current !== null) {
                window.clearInterval(pingTimerRef.current);
            }
            pingTimerRef.current = window.setInterval(() => {
                try {
                    ws.send(JSON.stringify({ type: "ping" }));
                }
                catch {
                    /* socket may already be closed */
                }
            }, pingIntervalMs);
        };
        ws.onmessage = (evt) => {
            if (!mountedRef.current)
                return;
            try {
                const parsed = JSON.parse(typeof evt.data === "string" ? evt.data : String(evt.data));
                // Ignore pong control frames.
                if (parsed && typeof parsed === "object" && parsed.type === "pong") {
                    return;
                }
                const env = coerce(parsed);
                if (env) {
                    setEnvelope(env);
                }
            }
            catch (err) {
                // Malformed frame — keep the socket, just log via lastError.
                setLastError(err instanceof Error ? err.message : String(err));
            }
        };
        ws.onerror = () => {
            if (!mountedRef.current)
                return;
            setLastError("WebSocket error");
            // onclose fires next; recovery lives there.
        };
        ws.onclose = () => {
            if (!mountedRef.current)
                return;
            wsRef.current = null;
            if (pingTimerRef.current !== null) {
                window.clearInterval(pingTimerRef.current);
                pingTimerRef.current = null;
            }
            // Only degrade if we were previously up — repeated close storms
            // should still show `disconnected` after several failed opens.
            setConnectionState((prev) => (prev === "connected" ? "polling" : "disconnected"));
            startPolling();
            scheduleReconnect();
        };
    }, [enabled, wsUrl, pingIntervalMs, startPolling]);
    const scheduleReconnect = useCallback(() => {
        if (!enabled)
            return;
        if (reconnectTimerRef.current !== null)
            return;
        const attempt = attemptsRef.current + 1;
        attemptsRef.current = attempt;
        setReconnectAttempts(attempt);
        const delay = Math.min(backoffMaxMs, backoffBaseMs * 2 ** (attempt - 1));
        reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
        }, delay);
    }, [backoffBaseMs, backoffMaxMs, enabled, connect]);
    // Boot / teardown.
    useEffect(() => {
        if (!enabled)
            return;
        mountedRef.current = true;
        // Kick off a REST fetch immediately so the UI is populated before WS opens.
        (async () => {
            try {
                const ac = new AbortController();
                abortRef.current = ac;
                const data = await getVehiclesLive({ signal: ac.signal });
                if (mountedRef.current) {
                    setEnvelope(coerce(data));
                }
            }
            catch (err) {
                if (isAbortError(err) || !mountedRef.current)
                    return;
                setLastError(err instanceof Error ? err.message : String(err));
            }
        })();
        connect();
        return () => {
            mountedRef.current = false;
            clearTimers();
            if (wsRef.current) {
                try {
                    wsRef.current.close();
                }
                catch {
                    /* noop */
                }
                wsRef.current = null;
            }
        };
    }, [enabled, connect, clearTimers]);
    // Publish a stable object — memoise so consumers can rely on referential equality per state change.
    return useMemo(() => ({
        envelope,
        connectionState,
        lastError,
        reconnectAttempts,
    }), [envelope, connectionState, lastError, reconnectAttempts]);
}
/** Exposed for testing — reveals the base URL calculation. */
export const __liveFeedBaseUrl = getBaseUrl;
